import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Form, Icon, Modal } from 'semantic-ui-react';
import Page from './Page';

export default function ForgotPassword() {
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [partialPhone, setPartialPhone] = useState('');
  const [tempEmail, setTempEmail] = useState('');

  const RECAPTCHA_SITE_KEY = '6LcU9k0rAAAAAG9dPhQK0nmuZH2YJ8_43r1oYw8C';

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  const getCaptchaToken = async (action) => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        reject(new Error('reCAPTCHA no cargado'));
        return;
      }
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
          resolve(token);
        } catch (error) {
          reject(error);
        }
      });
    });
  };

  const validateEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const captchaToken = await getCaptchaToken('validate_email');
      const response = await fetch(`/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, confirmed: false, captchaToken: captchaToken })
      });
      const data = await response.json();

      if (data.needsConfirmation) {
        setPartialPhone(data.partialPhone);
        setTempEmail(email);
        setShowConfirmModal(true);
      } else if (data.success === false) {
        setError(data.message);
      } else if (!data.success) {
        setError(data.message || 'Error al verificar el email');
      }
    } catch (err) {
      setError(`Error de conexión: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmAndSend = async () => {
    setShowConfirmModal(false);
    setLoading(true);

    try {
      const captchaToken = await getCaptchaToken('send_sms');
      const response = await fetch(`/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: tempEmail, confirmed: true, captchaToken: captchaToken })
      });
      const data = await response.json();

      if (data.success) {
        setSent(true);
        setMessage(data.message);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#f3f4f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    },
    card: {
      background: 'white',
      borderRadius: '24px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      padding: 'clamp(30px, 5vw, 50px)',
      maxWidth: '500px',
      width: '100%',
      animation: 'fadeInUp 0.5s ease-out'
    },
    header: {
      marginBottom: 'clamp(25px, 5vh, 40px)',
      textAlign: 'center'
    },
    title: {
      fontSize: 'clamp(1.8em, 5vw, 2.5em)',
      fontWeight: 700,
      color: '#1f2937',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      flexWrap: 'wrap'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: 'clamp(0.85em, 3vw, 1em)',
      fontWeight: 400,
      marginTop: '8px'
    },
    inputWrapper: {
      marginBottom: '24px'
    },
    messageSuccess: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      padding: '14px 18px',
      borderRadius: '12px',
      marginBottom: '24px',
      textAlign: 'center',
      fontSize: '0.9em',
      borderLeft: '4px solid #10b981'
    },
    messageError: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      padding: '14px 18px',
      borderRadius: '12px',
      marginBottom: '24px',
      textAlign: 'center',
      fontSize: '0.9em',
      borderLeft: '4px solid #ef4444'
    },
    captchaText: {
      fontSize: '12px',
      color: '#9ca3af',
      textAlign: 'center',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px'
    },
    backLink: {
      textAlign: 'center',
      marginTop: '24px'
    },
    modalContent: {
      fontSize: '14px',
      color: '#666',
      marginTop: '10px'
    }
  };

  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .forgot-form-input input {
        border-radius: 12px !important;
        border: 2px solid #e5e7eb !important;
        padding: 14px 16px !important;
        font-size: 1em !important;
        transition: all 0.3s ease !important;
        background-color: #f9fafb !important;
      }
      .forgot-form-input input:focus {
        border-color: #667eea !important;
        background-color: white !important;
        box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1) !important;
        outline: none !important;
      }
      .forgot-form-input .icon {
        color: #667eea !important;
      }
      .forgot-button {
        background: #667eea !important;
        border: none !important;
        border-radius: 12px !important;
        padding: 16px !important;
        font-size: 1.1em !important;
        font-weight: 600 !important;
        color: white !important;
        transition: all 0.3s ease !important;
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4) !important;
      }
      .forgot-button:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5) !important;
        background: #5568d3 !important;
      }
      .forgot-button:active {
        transform: translateY(0) !important;
      }
      .forgot-link {
        color: #667eea;
        text-decoration: none;
        font-size: 0.9em;
        transition: all 0.3s ease;
        font-weight: 500;
      }
      .forgot-link:hover {
        color: #5568d3;
        text-decoration: underline;
      }
      @media (max-width: 480px) {
        .forgot-button {
          padding: 14px !important;
          font-size: 1em !important;
        }
        .forgot-form-input input {
          padding: 12px 14px !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (sent) {
    return (
      <Page>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.header}>
              <div style={styles.title}>
                <Icon name="mail outline" size="large" style={{ color: '#10b981' }} />
                <span>¡Código Enviado!</span>
              </div>
              <div style={styles.subtitle}>
                Revisa tu teléfono con las instrucciones
              </div>
            </div>

            <div style={styles.messageSuccess}>
              <Icon name="check circle" />
              {message}
            </div>

            <Button
              className="forgot-button"
              fluid
              size='large'
              onClick={() => history.push('/')}
            >
              <Icon name="arrow left" />
              Volver al login
            </Button>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.title}>
              <Icon name="key" size="large" />
              <span style={{ whiteSpace: 'nowrap' }}>Recuperar Contraseña</span>
            </div>
            <div style={styles.subtitle}>
              Ingresa tu Email corporativo para recibir instrucciones
            </div>
          </div>

          {error && (
            <div style={styles.messageError}>
              <Icon name="exclamation circle" />
              {error}
            </div>
          )}

          <Form size='large' loading={loading} onSubmit={validateEmail}>
            <div style={styles.inputWrapper}>
              <Form.Input
                className="forgot-form-input"
                fluid
                icon='mail'
                iconPosition='left'
                placeholder='Email corporativo'
                value={email}
                onChange={e => setEmail(e.target.value)}
                type="email"
                required
              />
            </div>

            <Button
              className="forgot-button"
              fluid
              size='large'
              type="submit"
              loading={loading}
            >
              <Icon name="send" />
              Enviar código
            </Button>

            <div style={styles.backLink}>
              <Link to="/" className="forgot-link">
                ← Volver al login
              </Link>
            </div>
          </Form>
        </div>
      </div>

      <Modal open={showConfirmModal} size='tiny'>
        <Modal.Header>Confirmar envío</Modal.Header>
        <Modal.Content>
          <p>¿Deseas enviar el mensaje de recuperación al número <strong>{partialPhone}</strong>?</p>
          <p style={styles.modalContent}>
            Solo tú recibirás este mensaje.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowConfirmModal(false)} style={{ background: '#f3f4f6', color: '#374151' }}>
            Cancelar
          </Button>
          <Button primary onClick={confirmAndSend} style={{ background: '#667eea', color: 'white' }}>
            <Icon name="send" />
            Enviar código
          </Button>
        </Modal.Actions>
      </Modal>
    </Page>
  );
}